import { Controller } from "@hotwired/stimulus"

// レシート数量管理用のStimulusコントローラー
// 数量選択、確認ダイアログ、サーバー保存、レシート表示更新を管理
export default class extends Controller {
  static targets = [
    "quantityDisplay",
    "quantityInput", 
    "incrementButton",
    "decrementButton",
    "submitButton",
    "receiptSection",
    "receiptItemsBody",
    "receiptEmptyMessage"
  ]
  
  declare readonly quantityDisplayTargets: HTMLElement[]
  declare readonly quantityInputTargets: HTMLInputElement[]
  declare readonly incrementButtonTargets: HTMLButtonElement[]
  declare readonly decrementButtonTargets: HTMLButtonElement[]
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly receiptSectionTarget: HTMLElement
  declare readonly receiptItemsBodyTarget: HTMLElement
  declare readonly receiptEmptyMessageTarget: HTMLElement
  
  // データ属性
  static values = {
    menuId: String,
    updateUrl: String
  }
  
  declare readonly menuIdValue: string
  declare readonly updateUrlValue: string
  
  // 各メニューアイテムの初期値を保持
  private originalQuantities: Map<string, number> = new Map()
  // 現在の値を保持
  private currentQuantities: Map<string, number> = new Map()
  // 最大値（ReceiptItemのバリデーションと同じ）
  private readonly MAX_QUANTITY = 10
  
  connect() {
    // 初期値を保存
    this.quantityInputTargets.forEach((input) => {
      const menuItemId = input.dataset.menuItemId || ""
      const quantity = parseInt(input.value) || 0
      this.originalQuantities.set(menuItemId, quantity)
      this.currentQuantities.set(menuItemId, quantity)
    })
    
    // 初期状態で変更がなければボタンを無効化
    this.updateSubmitButton()
  }
  
  // 数量増加
  increment(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const menuItemId = button.dataset.menuItemId || ""
    this.changeQuantity(menuItemId, 1)
  }
  
  // 数量減少
  decrement(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const menuItemId = button.dataset.menuItemId || ""
    this.changeQuantity(menuItemId, -1)
  }
  
  // 数量変更
  private changeQuantity(menuItemId: string, delta: number) {
    const currentQuantity = this.currentQuantities.get(menuItemId) || 0
    const newQuantity = Math.max(0, Math.min(currentQuantity + delta, this.MAX_QUANTITY))
    
    if (newQuantity === currentQuantity) return
    
    // 値を更新
    this.currentQuantities.set(menuItemId, newQuantity)
    
    // UI更新
    this.updateQuantityDisplay(menuItemId, newQuantity)
    
    // 送信ボタンの状態更新
    this.updateSubmitButton()
  }
  
  // 数量表示を更新
  private updateQuantityDisplay(menuItemId: string, quantity: number) {
    // 表示要素を更新
    const display = this.quantityDisplayTargets.find(
      el => el.dataset.menuItemId === menuItemId
    )
    if (display) {
      display.textContent = quantity.toString()
    }
    
    // input要素も更新
    const input = this.quantityInputTargets.find(
      el => el.dataset.menuItemId === menuItemId
    )
    if (input) {
      input.value = quantity.toString()
    }
  }
  
  // 送信ボタンの状態を更新
  private updateSubmitButton() {
    const hasChanges = this.hasChanges()
    
    if (hasChanges) {
      this.submitButtonTarget.classList.remove('uk-button-muted')
      this.submitButtonTarget.classList.add('uk-button-secondary')
      this.submitButtonTarget.disabled = false
    } else {
      this.submitButtonTarget.classList.remove('uk-button-secondary')
      this.submitButtonTarget.classList.add('uk-button-muted')
      this.submitButtonTarget.disabled = true
    }
  }
  
  // 変更があるかチェック
  private hasChanges(): boolean {
    for (const [menuItemId, currentQuantity] of this.currentQuantities) {
      const originalQuantity = this.originalQuantities.get(menuItemId) || 0
      if (currentQuantity !== originalQuantity) {
        return true
      }
    }
    return false
  }
  
  // 変更を反映
  async submitChanges(event: Event) {
    event.preventDefault()
    
    if (!this.hasChanges()) return
    
    // 0以外から変更されるアイテムがある場合は確認
    const needsConfirmation = this.needsConfirmation()
    
    if (needsConfirmation) {
      const confirmMessage = '既に数量が設定されているアイテムがあります。上書きしてもよろしいですか？'
      if (!confirm(confirmMessage)) {
        return
      }
    }
    
    await this.saveQuantities()
  }
  
  // 確認が必要かチェック
  private needsConfirmation(): boolean {
    for (const [menuItemId, currentQuantity] of this.currentQuantities) {
      const originalQuantity = this.originalQuantities.get(menuItemId) || 0
      
      // 元が0以外で、値が変更されている場合
      if (originalQuantity > 0 && currentQuantity !== originalQuantity) {
        return true
      }
    }
    return false
  }
  
  // サーバーに数量を保存
  private async saveQuantities() {
    // ボタンを無効化
    this.submitButtonTarget.disabled = true
    
    // 送信データを準備
    const quantities: Record<string, number> = {}
    this.currentQuantities.forEach((quantity, menuItemId) => {
      quantities[menuItemId] = quantity
    })
    
    try {
      // CSRFトークンを取得
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          quantities: quantities
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // 成功時の処理
        // 初期値を更新
        this.currentQuantities.forEach((quantity, menuItemId) => {
          this.originalQuantities.set(menuItemId, quantity)
        })
        
        // レシート表示を更新
        this.updateReceiptDisplay(data)
        
        // ボタンスタイルをリセット
        this.updateSubmitButton()
        
        // 成功メッセージを表示
        this.showMessage(data.message || '数量を更新しました', 'success')
      } else {
        // エラー時の処理
        console.error('Update failed:', data.errors)
        
        // エラーメッセージを表示
        const errorMessage = data.errors?.join(', ') || 'エラーが発生しました'
        this.showMessage(errorMessage, 'danger')
      }
    } catch (error) {
      console.error('Network error:', error)
      
      // ネットワークエラーメッセージを表示
      this.showMessage('通信エラーが発生しました', 'danger')
    } finally {
      // ボタンを再度有効化
      this.submitButtonTarget.disabled = false
    }
  }
  
  // レシート表示を更新
  private updateReceiptDisplay(data: any) {
    const items = data.items || []

    // Clear existing content
    this.receiptItemsBodyTarget.innerHTML = ''

    if (items.length === 0) {
      // アイテムがない場合
      const emptyRow = document.createElement('tr')
      const emptyCell = document.createElement('td')
      emptyCell.className = 'uk-text-center uk-text-muted'
      emptyCell.colSpan = 2
      emptyCell.textContent = 'まだレシートが作成されていません'
      emptyRow.appendChild(emptyCell)
      this.receiptItemsBodyTarget.appendChild(emptyRow)
    } else {
      // アイテムがある場合 (XSS-safe using textContent)
      items.forEach((item: any) => {
        const row = document.createElement('tr')

        const nameCell = document.createElement('td')
        nameCell.textContent = item.menu_item_name
        row.appendChild(nameCell)

        const quantityCell = document.createElement('td')
        quantityCell.className = 'uk-text-center'
        quantityCell.textContent = `×${item.quantity}`
        row.appendChild(quantityCell)

        this.receiptItemsBodyTarget.appendChild(row)
      })
    }

    // プレビューモーダル内も更新（今後実装予定）
    // this.updateReceiptPreview(items)
  }
  
  // メッセージを表示（UIKit notification使用）
  private showMessage(message: string, status: 'success' | 'danger') {
    if (typeof window.UIkit !== 'undefined') {
      window.UIkit.notification({
        message: message,
        status: status,
        pos: 'top-right',
        timeout: 3000
      })
    }
  }
}